import { Routes } from '@angular/router';
import { pageHasChangesGuard } from '@guards/page-has-changes.guard';
import { hasCompanyAccessGuard } from '@services/company-scope';
import { hasClientAccessGuard, hasProjectAdminAccessGuard } from '@services/project-scope';
import { companySelectionGuard } from './pages/company-selection';
import { roleStatusSyncGuard } from './pages/companies/role-status-sync.guard';
import { authGuard } from './auth';

const authenticatedRoutes: Routes = [
  {
    path: 'projects/:projectId/companies/add',
    loadComponent: () =>
      import('./pages/companies/company-edit/company-edit.page').then((m) => m.CompanyEditPage),
    canActivate: [hasProjectAdminAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/edit',
    loadComponent: () =>
      import('./pages/companies/company-edit/company-edit.page').then((m) => m.CompanyEditPage),
    canActivate: [hasProjectAdminAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/manage-users',
    loadComponent: () =>
      import('./pages/companies/manage-company-users/manage-company-users.page').then(
        (m) => m.ManageCompanyUsersPage,
      ),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/doors/:doorId/messages',
    loadComponent: () => import('./pages/messages/messages.page').then((m) => m.MessagesPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/messages',
    loadComponent: () => import('./pages/messages/messages.page').then((m) => m.MessagesPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/doors/:doorId/notes',
    loadComponent: () => import('./pages/notes/notes.page').then((m) => m.NotesPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/notes',
    loadComponent: () => import('./pages/notes/notes.page').then((m) => m.NotesPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/doors',
    loadComponent: () => import('./pages/doors/doors.page').then((m) => m.DoorsPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies',
    loadComponent: () => import('./pages/companies/companies.page').then((m) => m.CompaniesPage),
    canActivate: [hasProjectAdminAccessGuard],
    canDeactivate: [roleStatusSyncGuard],
  },
  {
    path: 'projects/:projectId/admins',
    loadComponent: () => import('./pages/admins/admins.page').then((m) => m.AdminsPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/trades',
    loadComponent: () => import('./pages/trades/trades.page').then((m) => m.TradesPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/dashboard',
    loadComponent: () =>
      import('./pages/project-dashboard/project-dashboard.page').then(
        (m) => m.ProjectDashboardPage,
      ),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/door-setup',
    loadComponent: () =>
      import('./pages/door-setup/door-setup/door-setup.page').then((m) => m.DoorSetupPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/doors/hardware',
    loadComponent: () =>
      import('./pages/door-setup/doors-hardware/doors-hardware.page').then(
        (m) => m.DoorsHardwarePage,
      ),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/doors',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'doors' },
    children: [],
  },
  {
    path: 'projects/:projectId/messages',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'messages' },
    children: [],
  },
  {
    path: 'projects/:projectId/notes',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'notes' },
    children: [],
  },
  {
    path: 'projects/:projectId/companies/:companyId/sprints/new',
    loadComponent: () =>
      import('./pages/sprints/add-update-sprint/add-update-sprint.page').then(
        (m) => m.AddUpdateSprintPage,
      ),
    canActivate: [hasCompanyAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/sprints/:sprintId/progress',
    loadComponent: () =>
      import('./pages/sprints/sprint-progress/sprint-progress.page').then((m) => m.SprintProgressPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/sprints/:sprintId',
    loadComponent: () =>
      import('./pages/sprints/add-update-sprint/add-update-sprint.page').then(
        (m) => m.AddUpdateSprintPage,
      ),
    canActivate: [hasCompanyAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/sprints',
    loadComponent: () => import('./pages/sprints/sprints.page').then((m) => m.SprintsPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/sprints',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'sprints' },
    children: [],
  },
  {
    path: 'projects/:projectId/companies/:companyId/hardware-mounting-minutes',
    loadComponent: () =>
      import('./pages/hardware-mounting-minutes/hardware-mounting-minutes.page').then(
        (m) => m.HardwareMountingMinutesPage,
      ),
    canActivate: [hasCompanyAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/hardware-mounting-minutes',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'hardware-mounting-minutes' },
    children: [],
  },
  {
    path: 'projects/:projectId/checklists/add',
    loadComponent: () =>
      import('./pages/checklists/checklist-edit/checklist-edit.page').then(
        (m) => m.ChecklistEditPage,
      ),
    canActivate: [hasProjectAdminAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/checklists/:checklistId',
    loadComponent: () =>
      import('./pages/checklists/checklist-edit/checklist-edit.page').then(
        (m) => m.ChecklistEditPage,
      ),
    canActivate: [hasProjectAdminAccessGuard],
    canDeactivate: [pageHasChangesGuard],
  },
  {
    path: 'projects/:projectId/checklists',
    loadComponent: () => import('./pages/checklists/checklists.page').then((m) => m.ChecklistsPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/sync-doors',
    loadComponent: () =>
      import('./pages/syncs/door-sync/door-sync.page').then((m) => m.DoorSyncPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/syncs/:syncId/logs',
    loadComponent: () =>
      import('./pages/syncs/sync-logs/sync-logs.page').then((m) => m.SyncLogsPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/syncs',
    loadComponent: () => import('./pages/syncs/syncs.page').then((m) => m.SyncsPage),
    canActivate: [hasProjectAdminAccessGuard],
  },
  {
    path: 'projects/:projectId/logs',
    loadComponent: () => import('./pages/logs/logs.page').then((m) => m.LogsPage),
    canActivate: [hasClientAccessGuard],
  },
  {
    path: 'projects/:projectId/companies/:companyId/reports',
    loadComponent: () => import('./pages/reports/reports.page').then((m) => m.ReportsPage),
    canActivate: [hasCompanyAccessGuard],
  },
  {
    path: 'projects/:projectId/reports',
    canActivate: [companySelectionGuard],
    data: { companyScopedPage: 'reports' },
    children: [],
  },
  {
    path: 'projects',
    loadComponent: () => import('./pages/projects/projects.page').then((m) => m.ProjectsPage),
  },
];

export const routes: Routes = [
  {
    path: 'auth-callback',
    loadComponent: () =>
      import('./pages/auth-callback/auth-callback').then((m) => m.AuthCallbackComponent),
  },
  { path: '', redirectTo: 'projects', pathMatch: 'full' },
  {
    path: '',
    canActivateChild: [authGuard],
    children: authenticatedRoutes,
  },
  { path: '**', redirectTo: 'projects' },
];
